import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

// import DeleteModal from "../../../../../components/common/DeleteModal";
import CategoryMangeDrawer from "../../components/sections/admin/Category/CategoryMangeDrawer";
import Table from "../../components/elements/table/Table";

import { useDebounce } from "../../hooks/useDebounce";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { getPaginationRange, handleFilterChange } from "../../helpers";
import TableControls from "../../components/elements/TableControls";

import {
  applicationDelete,
  applicationList,
  applicationStatusChange,
  savedList,
} from "../../services/applicationService";

import { useNavigate, useSearchParams } from "react-router-dom";

import { useSocket } from "../../providers/SocketContext";
import DeleteModal from "../../components/common/DeleteModal";

const SavedJobs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const jobIdFromUrl = searchParams.get("jobId");
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const userLang = localStorage.getItem("lang") || "en";
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
    ...(jobIdFromUrl && { job: [jobIdFromUrl] }),
  });

  const handleBadgeClick = (e, id) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + window.scrollX,
    });
    setActiveTooltip((prev) => (prev === id ? null : id));
  };

  const removeJobFilter = () => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters.job;
      return { ...newFilters, page: 1 };
    });

    // Remove jobId from URL
    searchParams.delete("jobId");
    setSearchParams(searchParams);
  };

  const { data, isLoading, updateData, isFetching } = useQuery({
    queryKey: ["standard", filters, search],
    queryFn: async () => {
      const { job = [], status = [], ...restFilters } = filters || {};

      const queryParams = {
        ...restFilters,
        ...(search && { keyword: search }),

        // MULTI JOB PARAMS
        ...Object.fromEntries(job.map((val, i) => [`job[${i}]`, val])),

        // OPTIONAL: if status ever becomes multiselect
        ...Object.fromEntries(status.map((val, i) => [`status[${i}]`, val])),
      };

      const res = await savedList(queryParams);

      if (res?.status !== 200) {
        toast.warning(res?.message);
      }

      return res?.docs;
    },
  });

  //   const { data: jobList } = useQuery({
  //     queryKey: ["job-list"],
  //     queryFn: async () => {
  //       const res = await postList({
  //         limit: 10000,
  //         // type: "standard-job",
  //       });
  //       if (res?.status !== 200) {
  //         toast.warning(res?.message);
  //       }
  //       return res?.docs;
  //     },
  //   });

  //   const filteredJobTitle = jobList?.data?.find(
  //     (item) => item?._id === jobIdFromUrl,
  //   )?.title;

  useSocket("saveJobResponse", (res) => {
    const newItem = res?.ApplicationData;

    if (!newItem) return;

    queryClient.setQueryData(["standard", filters, search], (oldData) => {
      if (!oldData) return oldData;

      const existingData = oldData?.data || [];

      // 🚫 prevent duplicate insert
      const alreadyExists = existingData.some(
        (item) => item?._id === newItem?._id,
      );
      if (alreadyExists) return oldData;

      return {
        ...oldData,
        data: [newItem, ...existingData], // insert on top
        metadata: {
          ...oldData.metadata,
          totalDocs: (oldData.metadata?.totalDocs || 0) + 1,
        },
      };
    });
  });

  const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
    useMutation({
      mutationFn: async ({ id, status }) => {
        queryClient.setQueryData(["standard", filters, search], (state) => {
          if (!state) return;

          const updatedData = state?.data?.map((item) =>
            item?._id === id
              ? {
                ...item,
                status: status,
              }
              : item,
          );

          return { ...state, data: updatedData };
        });
        const res = await applicationStatusChange({ ids: [id], status });

        return { res, payload: { id, status } };
      },

      onSuccess: ({ res, payload }) => {
        if (res?.status === 200) {
          toast.success("Job Department status change successfully.");
        } else {
          queryClient.setQueryData(["standard", filters, search], (state) => {
            if (!state) return;

            const revertedData = state?.data?.map((item) =>
              item?._id === payload.id
                ? {
                  ...item,
                  status: item.status,
                }
                : item,
            );

            return { ...state, data: revertedData };
          });

          toast.error(res?.message || "Failed to update status.");
        }
      },

      onError: (error) => {
        toast.error(
          error?.message || "Something went wrong while updating status.",
        );
        console.error("Status update error:", error);
      },
    });

  const tableHeadData = [
    {
      _id: 1,
      name: "Job",
      align: "left",
      isFilter: false,
      onFilter: (val) => {
        if (val?.length > 0) {
          setFilters((pre) => ({
            ...pre,
            job: val.map((item) => item.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            const updated = { ...pre };
            delete updated.job;
            return { ...updated, page: 1 };
          });
        }
      },

      //   filterData:
      //     jobList?.data?.map((item) => {
      //       return {
      //         name: item?.content?.en?.title,
      //         value: item?._id,
      //         type: "checkbox",
      //       };
      //     }) || [],
    },
    {
      _id: 2,
      name: "Company Name",
      align: "left",
    },
    // {
    //   _id: 3,
    //   name: "Applicant Email",
    //   align: "left",
    // },
    // {
    //   _id: 3.5,
    //   name: "Mobile No.",
    //   align: "left",
    // },
    // {
    //   _id: 4,
    //   name: "Resume",
    //   align: "left",
    // },
    {
      _id: 5,
      name: "Saved On",
      align: "left",
      isSort: false,
      onSort: (val) => {
        if (val === "DESC") {
          setFilters((pre) => ({ ...pre, sortQuery: "-createdAt" }));
        } else {
          setFilters((pre) => ({ ...pre, sortQuery: "createdAt" }));
        }
      },
    },

    // {
    //   _id: 6,
    //   name: "Status",
    //   align: "left",
    //   isFilter: false,
    //   isFilterSearch: false,
    //   onFilter: (val) => {
    //     if (val?.length > 0 && val?.length === 1) {
    //       setFilters((pre) => ({
    //         ...pre,
    //         status: val?.map((item) => item?.value),
    //         page: 1,
    //       }));
    //     } else {
    //       setFilters((pre) => {
    //         delete pre?.status;
    //         return { ...pre };
    //       });
    //     }
    //   },
    //   filterData: status_filter || [],
    // },
    // {
    //   _id: 8,
    //   name: "Action",
    //   align: "left",
    // },
  ];

  const statusArray = [
    {
      state: "submitted",
      color: "info",
    },
    {
      state: "reviewed",
      color: "indigo",
    },
    {
      state: "shortlisted",
      color: "warning",
    },
    {
      state: "hired",
      color: "success",
    },
    {
      state: "rejected",
      color: "danger",
    },
    {
      state: "withdrawn",
      color: "stone",
    },
  ];
  const isImageFile = (ext = "") => {
    return ["jpg", "jpeg", "png", "webp", "gif"].includes(ext.toLowerCase());
  };
  const downloadFile = async (url, filename = "attachment") => {
    try {
      const res = await fetch(url, { mode: "cors" });
      const blob = await res.blob();

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error("Unable to download file");
    }
  };

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.job?.content?.en?.title || "-",
        },
        {
          _id: 2,
          type: "user",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.organization?.content?.en?.title || "-",
          image: item?.organization?.logo?.url,
        },
        // {
        //   _id: 3,
        //   type: "text",
        //   align: "left",
        //   cellClass: "!min-w-[100px]",
        //   data: item?.applicant?.email || "-",
        // },
        // {
        //   _id: 3.5,
        //   type: "text",
        //   align: "left",
        //   cellClass: "!min-w-[120px]",
        //   data: item?.applicant?.phone
        //     ? item.applicant.phone?.fullNumber
        //       ? item.applicant.phone?.fullNumber
        //       : `${item.applicant.phone.countryCode ?? ""} ${item.applicant.phone.number ?? ""}`
        //     : "-",
        // },
        // {
        //   _id: 4,
        //   type: "data",
        //   align: "left",
        //   cellClass: "!min-w-[240px]",
        //   data: (
        //     <div className="flex items-center gap-3">
        //       {item?.attachments?.slice(0, 1).map((file) => {
        //         const isImage = isImageFile(file?.extension);

        //         return (
        //           <div
        //             key={file?._id}
        //             className="
        //       flex items-center gap-3
        //     "
        //           >
        //             {/* REAL IMAGE PREVIEW */}
        //             {isImage ? (
        //               <div className="relative w-14 h-14 rounded-md overflow-hidden border">
        //                 <img
        //                   src={file.url}
        //                   alt={file.filename}
        //                   className="w-full h-full object-cover"
        //                 />

        //                 {/* Download overlay */}
        //                 <div
        //                   onClick={() => downloadFile(file.url, file.filename)}
        //                   className="
        //             absolute inset-0 bg-black/40
        //             flex items-center justify-center
        //             opacity-0 group-hover:opacity-100
        //             transition-opacity cursor-pointer
        //           "
        //                 >
        //                   <Download size={20} className="text-white" />
        //                 </div>
        //               </div>
        //             ) : (
        //               /* NON-IMAGE (PDF / DOC) */
        //               <div
        //                 onClick={() => downloadFile(file.url, file.filename)}
        //                 className="
        //           flex items-center gap-3 px-3 py-2
        //           border rounded-md cursor-pointer
        //           hover:bg-gray-50
        //         "
        //               >
        //                 <FileText size={22} className="text-primary" />
        //                 <div className="flex flex-col">
        //                   <span className="text-sm font-medium truncate max-w-[120px]">
        //                     {file.filename}
        //                   </span>
        //                   <span className="text-xs text-gray-500 uppercase">
        //                     {file.extension}
        //                   </span>
        //                 </div>
        //                 <Download size={18} className="ml-auto text-gray-400" />
        //               </div>
        //             )}
        //           </div>
        //         );
        //       })}

        //       {item?.attachments?.length > 1 && (
        //         <span className="ml-2 text-xs font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-700 border">
        //           +{item.attachments.length - 1}
        //         </span>
        //       )}
        //     </div>
        //   ),
        // },

        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.createdAt
            ? dayjs(item?.createdAt).format("DD MMM, YYYY")
            : "-",
        },

        // {
        //   _id: 5,
        //   type: "text",
        //   align: "left",
        //   cellClass: "!min-w-[100px]",
        //   data: (
        //     <div className="relative">
        //       <div
        //         className="flex gap-2 items-center cursor-pointer"
        //         // onClick={(e) => handleBadgeClick(e, item._id)}
        //       >
        //         <Badge
        //           type={
        //             statusArray.find((st) => st.state === item?.status)?.color
        //           }
        //           label={
        //             statusArray.find((st) => st.state === item?.status)?.state
        //           }
        //         />
        //       </div>

        //       {activeTooltip === item._id && (
        //         <ReactPortal
        //           position={tooltipPosition}
        //           onClose={() => setActiveTooltip(null)}
        //         >
        //           {statusArray.map((status) => {
        //             return (
        //               <StatusChangeTooltip
        //                 statusItem={status}
        //                 itemId={item._id}
        //                 statusChange={statusChangeHandler}
        //                 setActiveTooltip={setActiveTooltip}
        //                 isActive={item.status === status.state}
        //               />
        //             );
        //           })}
        //         </ReactPortal>
        //       )}
        //     </div>
        //   ),
        // },
        // {
        //   _id: 6,
        //   type: "action",
        //   align: "left",
        //   cellClass: "w-[5%]",
        //   actionData: [
        //     // {
        //     //   _id: 3, // make sure this _id is unique in the action array
        //     //   name: "View Applications",
        //     //   icon: "fa-regular fa-eye", // Eye icon for viewing
        //     //   onClick: () => {
        //     //     navigate(`/admin/application-view/${item?.uid}`);
        //     //   },
        //     //   standout: false,
        //     // },
        //     // {
        //     //   _id: 4,
        //     //   name: "Delete",
        //     //   icon: "fa-regular fa-trash-can",
        //     //   standout: true,
        //     //   onClick: () => {
        //     //     setDeleteModal({
        //     //       isOpen: true,
        //     //       data: item,
        //     //     });
        //     //   },
        //     // },
        //   ],
        // },
      ],
    };
  });

  // add or edit Response =================>
  async function taxonomyManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          queryClient.setQueryData(["department", filters, search], (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = [...stateData, res?.data];
              return { ...state, data: stateData };
            }
          });
          await queryClient.refetchQueries(["department", filters, search]);
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }

      if (res?.status === 200) {
        queryClient.setQueryData(["department", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = stateData?.map((item) =>
              item?._id === res?.data?._id ? { ...item, ...res?.data } : item,
            );
            return { ...state, data: stateData };
          }
        });
        await queryClient.refetchQueries(["department", filters, search]);
      }

      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  // category delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await applicationDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return { res, payload: Array.isArray(payload) ? payload : [payload] };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        queryClient.setQueryData(["standard", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || [])?.filter(
              (item) => !payload?.includes(item?._id),
            );
            return { ...state, data: stateData };
          }
        });
        toast.success("Application deleted successfully.");

        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["standard", filters, search]);
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
      toast.error(
        error?.message || "Something went wrong while delete department.",
      );
    },
  });

  return (
    <div>
      <div className="space-y-4 w-full flex flex-col pb-10">
        <div className="space-y-8">
          <div className="flex items-center gap-3">
            <TableControls
              limit={filters?.limit}
              totalDocs={data?.metadata?.totalDocs}
              onLimitChange={(val) =>
                handleFilterChange("limit", val, setFilters)
              }
              onSearch={(val) => {
                setKeyword(val);
                handleFilterChange("page", 1, setFilters);
              }}
            />
            {/* {jobIdFromUrl && filteredJobTitle && (
              <div className="inline-flex items-center px-4 py-1 bg-emerald-100 text-emerald-800 rounded-full text-base font-medium hover:bg-emerald-200 transition-colors">
                <span className="mr-3">{filteredJobTitle}</span>
                <button
                  onClick={removeJobFilter}
                  className="flex-shrink-0 ml-1 h-5 w-5 rounded-full inline-flex items-center justify-center hover:bg-emerald-300 focus:outline-none focus:bg-emerald-300"
                >
                  <X size={14} />
                </button>
              </div>
            )} */}
          </div>
        </div>

        {/* Applications Card List */}
        <div className="space-y-3">
          {isLoading ? (
            <p className="text-center py-10">Loading saved jobs...</p>
          ) : data?.data?.length === 0 ? (
            <p className="text-center py-10 text-gray-500">
              No saved jobs found
            </p>
          ) : (
            data?.data?.map((item) => (
              <div
                key={item?._id}
                className="mt-3 border border-gray-300 shadow-[0_0_8px_rgba(0,0,0,0.1)] rounded-xl p-3 flex items-center justify-between bg-white"
              >
                {/* Left Side */}
                <div className="flex items-center gap-4">
                  <img
                    src={item?.organization?.logo?.url}
                    alt="company"
                    className="w-14 h-14 rounded object-cover flex-shrink-0"
                  />

                  <div className="min-w-0">
                    <h4 className="text-sm font-semibold text-gray-800 break-all">
                      {item?.job?.content?.[userLang]?.title || "-"}
                    </h4>

                    {/* <p className="text-sm text-gray-600">
                    {item?.job?.experience?.content?.[userLang]?.title || "-"}
                  </p> */}

                    <p className="text-xs text-gray-700 font-medium mt-1">
                      Job by{" "}
                      {item?.organization?.name ||
                        item?.organization?.content?.[userLang]?.title ||
                        "-"}
                    </p>

                    <p className="text-xs text-gray-500 mt-1">
                      Published on{" "}
                      {item?.createdAt
                        ? dayjs(item?.createdAt).format("DD MMM, YYYY")
                        : "-"}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {data?.metadata?.totalPages > 1 && (
          <div className="flex items-center justify-between mt-8">
            <div className="text-sm text-gray-500">
              Showing{" "}
              {getPaginationRange(
                filters?.page,
                filters?.limit,
                data?.metadata?.totalDocs,
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Previous */}
              <button
                disabled={filters.page === 1}
                onClick={() =>
                  handleFilterChange("page", filters.page - 1, setFilters)
                }
                className={`px-3 py-1 rounded-md border text-sm ${filters.page === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white hover:bg-gray-100"
                  }`}
              >
                Prev
              </button>

              {/* Page Numbers */}
              {Array.from(
                { length: data?.metadata?.totalPages },
                (_, i) => i + 1,
              ).map((page) => (
                <button
                  key={page}
                  onClick={() => handleFilterChange("page", page, setFilters)}
                  className={`px-3 py-1 rounded-md text-sm border ${filters.page === page
                      ? "bg-palatte text-white border-palatte"
                      : "bg-white hover:bg-gray-100"
                    }`}
                >
                  {page}
                </button>
              ))}

              {/* Next */}
              <button
                disabled={filters.page === data?.metadata?.totalPages}
                onClick={() =>
                  handleFilterChange("page", filters.page + 1, setFilters)
                }
                className={`px-3 py-1 rounded-md border text-sm ${filters.page === data?.metadata?.totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white hover:bg-gray-100"
                  }`}
              >
                Next
              </button>
            </div>
          </div>
        )}

        <CategoryMangeDrawer
          open={modalOpen?.isOpen}
          data={modalOpen?.data}
          close={() => setModalOpen({ isOpen: false, data: null })}
          getResponse={taxonomyManageResponse}
          type={"department"}
          title={"Job Skills"}
        />
        <DeleteModal
          open={deleteModal?.isOpen}
          loading={deletePending}
          onClose={() =>
            setDeleteModal((pre) => ({ data: null, isOpen: false }))
          }
          deleteHandler={() => {
            deleteHandler(deleteModal?.data?._id);
          }}
        />
      </div>
    </div>
  );
};

export default SavedJobs;
